// src/hooks/usePersons.js - Versión corregida
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchPersons,
  fetchPersonById,
  createPerson,
  updatePerson,
  deletePerson,
  fetchPersonByDNI,
  updatePersonLocation,
  deletePersonLocation,
  createPersonLocationFull,
} from '../api/administrationService';

// Query keys centralizados
const queryKeys = {
  persons: ['persons'],
  personDetail: (id) => ['persons', 'detail', id],
  personByDNI: (dni) => ['persons', 'dni', dni],
};

export const usePersons = () => {
  const queryClient = useQueryClient();

  const personsQuery = useQuery({
    queryKey: queryKeys.persons,
    queryFn: fetchPersons,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
  });

  const refetchPersons = () => {
    return queryClient.invalidateQueries({ queryKey: queryKeys.persons });
  };

  const deleteMutation = useMutation({
    mutationFn: deletePerson,
    onSuccess: () => {
      refetchPersons();
    },
  });

  return {
    persons: personsQuery.data ?? [],
    error: personsQuery.error,
    isLoading: personsQuery.isLoading,
    isFetching: personsQuery.isFetching,
    refetchPersons,
    deleteMutation,
  };
};

// Hook separado para búsqueda por DNI (no anidado)
export const useFetchPersonByDNI = (dni, options = {}) => {
  return useQuery({
    queryKey: queryKeys.personByDNI(dni),
    queryFn: () => fetchPersonByDNI(dni),
    enabled: !!dni && options.enabled,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const usePersonForm = (personId) => {
  const queryClient = useQueryClient();
  const isEdit = Boolean(personId);

  const personDetailQuery = useQuery({
    queryKey: queryKeys.personDetail(personId),
    queryFn: () => fetchPersonById(personId),
    enabled: isEdit,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const refreshPerson = async () => {
    if (!personId) return null;

    // Invalidar y refetch
    await queryClient.invalidateQueries({
      queryKey: queryKeys.personDetail(personId)
    });

    const result = await queryClient.fetchQuery({
      queryKey: queryKeys.personDetail(personId),
      queryFn: () => fetchPersonById(personId),
    });

    return result;
  };

  const createMutation = useMutation({
    mutationFn: createPerson,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.persons });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updatePerson(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.persons });
      queryClient.invalidateQueries({ queryKey: queryKeys.personDetail(variables.id) });
    },
  });

  const createLocationFullMutation = useMutation({
    mutationFn: ({ personId, data }) => createPersonLocationFull(personId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.persons });
      queryClient.invalidateQueries({ queryKey: queryKeys.personDetail(variables.personId) });
    },
  });

  const updateLocationMutation = useMutation({
    mutationFn: ({ personId, locId, data }) => updatePersonLocation(personId, locId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.persons });
      queryClient.invalidateQueries({ queryKey: queryKeys.personDetail(variables.personId) });
    },
  })

  const deleteLocationMutation = useMutation({
    mutationFn: ({ personId, locId }) => deletePersonLocation(personId, locId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.persons });
      queryClient.invalidateQueries({ queryKey: queryKeys.personDetail(variables.personId) });
    },
  });

  return {
    isEdit,
    personDetail: personDetailQuery.data,
    isLoadingDetail: personDetailQuery.isLoading,
    isFetchingDetail: personDetailQuery.isFetching,
    refreshPerson,
    createMutation,
    updateMutation,
    createLocationFullMutation,
    updateLocationMutation,
    deleteLocationMutation,
    isBusy:
      createLocationFullMutation.isPending ||
      updateLocationMutation.isPending ||
      deleteLocationMutation.isPending,
  };
};